import { useEffect, useRef, useState } from 'react';
import type { HitoEstado } from '@/types';
import { ESTADO_META } from './EstadoSelect';
import styles from './EstadoModal.module.css';

interface Props {
  open: boolean;
  hitoTitulo: string;
  estadoAnterior: HitoEstado | null;
  estadoNuevo: HitoEstado | null;
  onCancel: () => void;
  onSubmit: (payload: { comentario: string; evidenciaUrl?: string }) => void;
  submitting?: boolean;
}

const ESTADO_ICONOS: Record<HitoEstado, string> = {
  pendiente: '⏸',
  on_track: '🚀',
  bloqueado: '🚫',
  suspendido: '⏯',
  entregado: '✓',
};

/**
 * Modal genérico de cambio de estado.
 * - Comentario obligatorio.
 * - URL opcional (validada http/https si está presente).
 * - Color / título / icono se adaptan al estado destino.
 */
export function EstadoModal({
  open,
  hitoTitulo,
  estadoAnterior,
  estadoNuevo,
  onCancel,
  onSubmit,
  submitting,
}: Props) {
  const [comentario, setComentario] = useState('');
  const [url, setUrl] = useState('');
  const [touched, setTouched] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (open) {
      setComentario('');
      setUrl('');
      setTouched(false);
      setTimeout(() => textareaRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open || !estadoNuevo) return null;

  const meta = ESTADO_META[estadoNuevo];
  const metaPrev = estadoAnterior ? ESTADO_META[estadoAnterior] : null;
  const comentarioInvalido = touched && comentario.trim().length === 0;
  const urlInvalido = url.length > 0 && !/^https?:\/\//i.test(url);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (comentario.trim().length === 0) {
      textareaRef.current?.focus();
      return;
    }
    if (urlInvalido) return;
    onSubmit({
      comentario: comentario.trim(),
      evidenciaUrl: url.trim() || undefined,
    });
  }

  return (
    <div className={styles.backdrop} onClick={onCancel}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="estado-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ borderTop: `4px solid ${meta.color}` }}
      >
        <div className={styles.header}>
          <div
            className={styles.icon}
            style={{ background: meta.bg, color: meta.color }}
          >
            {ESTADO_ICONOS[estadoNuevo]}
          </div>
          <div>
            <h2 id="estado-modal-title" className={styles.title}>
              Marcar como {meta.label}
            </h2>
            <div className={styles.subtitle}>{hitoTitulo}</div>
            {metaPrev && (
              <div className={styles.transition}>
                <span
                  className={styles.pillEstado}
                  style={{ background: metaPrev.bg, color: metaPrev.color }}
                >
                  {metaPrev.label}
                </span>
                <span className={styles.arrow}>→</span>
                <span
                  className={styles.pillEstado}
                  style={{ background: meta.bg, color: meta.color }}
                >
                  {meta.label}
                </span>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>
            Comentario <span className={styles.required}>*</span>
            <textarea
              ref={textareaRef}
              className={`${styles.textarea} ${comentarioInvalido ? styles.invalid : ''}`}
              rows={4}
              maxLength={500}
              placeholder={getPlaceholder(estadoNuevo)}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              onBlur={() => setTouched(true)}
            />
            <div className={styles.hint}>
              {comentarioInvalido && (
                <span className={styles.error}>El comentario es obligatorio.</span>
              )}
              <span className={styles.counter}>{comentario.length}/500</span>
            </div>
          </label>

          <label className={styles.label}>
            Link de respaldo <span className={styles.optional}>(opcional)</span>
            <input
              type="url"
              className={`${styles.input} ${urlInvalido ? styles.invalid : ''}`}
              maxLength={500}
              placeholder="https://drive.google.com/… · https://sharepoint.com/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <div className={styles.hint}>
              {urlInvalido ? (
                <span className={styles.error}>Debe comenzar con http:// o https://</span>
              ) : (
                <span className={styles.helperMuted}>
                  Google Drive, SharePoint, OneDrive o cualquier link público.
                </span>
              )}
            </div>
          </label>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={submitting || comentario.trim().length === 0 || urlInvalido}
              style={{ background: meta.color }}
            >
              {submitting ? 'Guardando…' : `Marcar como ${meta.label}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function getPlaceholder(estado: HitoEstado): string {
  switch (estado) {
    case 'bloqueado':
      return 'Describe por qué se está bloqueando este hito…';
    case 'suspendido':
      return 'Explica el motivo de la suspensión…';
    case 'entregado':
      return 'Detalles de la entrega (entregables, validación, etc.)…';
    case 'on_track':
      return 'Comenta el avance: hitos cumplidos, próximos pasos…';
    case 'pendiente':
      return 'Motivo para volver a pendiente…';
    default:
      return 'Comentario sobre el cambio de estado…';
  }
}
